import {
  Box,
  Button,
  DialogContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { OrganizationUserContext } from "contexts/Organization";
import { CheckoutState, SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { BannerCouponName } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { DISPLAYED_ONBOARDING_PAYWALL } from "utils/localStorage";
import { getNextFriday } from "utils/time";
import { trackEvent } from "utils/tracking";

import { BannerCouponSnippets } from "../Plan/helpers";
import ProCard from "./components/ProCard";
import VaCard from "./components/VaCard";
import OnboardingPaywallMobile from "./mobile";
import styles from "./styles";

const OnboardingPaywall = () => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { currentOrg } = useContext(OrganizationUserContext);
  const {
    plans,
    setOpenCheckoutDialog,
    setUpgradeDialogSource,
    subscription,
    isProPlan,
    isVaPlan,
  } = useContext(SubscriptionContext);

  const isAlreadyOnPro = currentOrg?.isPaidUser && isProPlan;
  const isAlreadyOnVa = currentOrg?.isPaidUser && isVaPlan;

  const isVaMoneyBack =
    subscription?.bannerPromoCode?.couponName ===
    BannerCouponName.money_back_guarantee;

  const handleCheckout = (plan: Plans) => {
    trackEvent("Onboarding Paywall - Upgrade Button Clicked", { plan });
    const state: CheckoutState = {
      plan,
      source: "Onboarding Paywall",
    };
    setOpenCheckoutDialog(state);
  };

  const closeDialog = () => {
    trackEvent("Onboarding Paywall - Close Button Clicked");
    localStorage.setItem(
      `${DISPLAYED_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
      "true",
    );
    setOpenCheckoutDialog(null);
    setUpgradeDialogSource(null);
  };

  const renderRow = (
    attribute: JSX.Element,
    freeValue: JSX.Element,
    proValue: JSX.Element,
    vaValue: JSX.Element,
    isLastRow?: boolean,
  ) => {
    return (
      <Grid container item gap={1} sx={styles.row(isLastRow)}>
        <Grid item xs sx={styles.cell}>
          <Typography sx={styles.attribute}>{attribute}</Typography>
        </Grid>
        <Grid item xs={2} container justifyContent={"center"} sx={styles.cell}>
          {freeValue}
        </Grid>

        <Grid
          item
          xs={3.8}
          container
          justifyContent={"center"}
          sx={styles.cellWithLeftAndRightBorder(isLastRow)}
        >
          {proValue}
        </Grid>

        <Grid
          item
          xs={3.8}
          container
          justifyContent={"center"}
          sx={styles.cellWithLeftAndRightBorder(isLastRow)}
        >
          {vaValue}
        </Grid>
      </Grid>
    );
  };

  if (!plans) {
    return <></>;
  }

  const renderDiscount = () => {
    if (isVaMoneyBack) return <></>;
    if (!subscription?.bannerPromoCode?.discountPercent) return <></>;
    return (
      <Typography
        textAlign={"center"}
        sx={{
          fontWeight: 600,
          fontSize: 11,
          mt: 1,
          textTransform: "uppercase",
          color: "black",
        }}
      >
        💥 {BannerCouponSnippets[subscription?.bannerPromoCode?.couponName]}:{" "}
        <Box component="span" sx={{ color: "#39a47e" }}>
          {subscription?.bannerPromoCode?.discountPercent}% off
        </Box>{" "}
        <Box component="span" sx={{ color: "#d95e58" }}>
          until {subscription?.bannerPromoCode?.dateEnding}
        </Box>
      </Typography>
    );
  };

  const renderEmptyLine = () => {
    return (
      <Typography>
        <Box
          component="i"
          className="fa-sharp-duotone fa-thin fa-dash"
          sx={{ color: grey[200], mr: 1 }}
        />
      </Typography>
    );
  };

  return isMobileScreen ? (
    <OnboardingPaywallMobile closeDialog={closeDialog} />
  ) : (
    <DialogContent>
      <IconButton sx={styles.closeButton} onClick={closeDialog}>
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>
      <Typography textAlign="center" variant="h3" gutterBottom>
        Unlock the power tools top creators use to grow faster ⚡
      </Typography>
      <Typography textAlign={"center"} sx={{ mb: 3, fontSize: 16 }}>
        Get deeper insights, reach premium brands, and automate your workflow.
      </Typography>

      <Grid>
        <Grid container item gap={1}>
          <Grid item xs />
          <Grid
            item
            xs={2}
            container
            justifyContent={"center"}
            alignItems="flex-end"
          >
            <Typography variant="h4">Free</Typography>
          </Grid>

          <Grid item xs={3.8} sx={styles.cardBorder}>
            <ProCard />
          </Grid>

          <Grid item xs={3.8} sx={styles.cardBorder}>
            <VaCard isVaMoneyBack={isVaMoneyBack} />
          </Grid>
        </Grid>
        {renderRow(
          <>🚀 Monthly Pitches</>,
          <> 20</>,
          <>1,000</>,
          <>1,000 (300 automated)</>,
        )}
        {renderRow(
          <>🧠 Brand Contacts Access</>,
          <> 50k</>,
          <> 600k+</>,
          <>All</>,
        )}
        {renderRow(
          <> 💎 Smart Filters & Insights</>,
          renderEmptyLine(),

          <Typography>
            <Box
              component="i"
              className="fa-solid fa-circle-check"
              sx={{ color: "green.main", mr: 1 }}
            />
            Included
          </Typography>,

          <>
            <Typography>
              <Box
                component="i"
                className="fa-solid fa-circle-check"
                sx={{ color: "green.main", mr: 1 }}
              />
              Included
            </Typography>
          </>,
        )}
        {renderRow(
          <> 💸 Brand Pay Data</>,
          renderEmptyLine(),
          renderEmptyLine(),
          <>
            <Typography>
              <Box
                component="i"
                className="fa-solid fa-circle-check"
                sx={{ color: "green.main", mr: 1 }}
              />
              Included
            </Typography>
          </>,
        )}
        {isVaMoneyBack &&
          renderRow(
            <> 💰 Money-Back Guaranteed</>,
            renderEmptyLine(),
            renderEmptyLine(),
            <>
              <Typography>
                <Box
                  component="i"
                  className="fa-solid fa-circle-check"
                  sx={{ color: "green.main", mr: 1 }}
                />
                Risk Free{" "}
                <Tooltip
                  slotProps={{
                    tooltip: {
                      sx: {
                        color: "black",
                        backgroundColor: "white",
                        border: `1px solid black`,
                      },
                    },
                  }}
                  title="If you don't earn $600+ in brand deals sourced by Bento, we'll refund you"
                >
                  <Box
                    component={"span"}
                    sx={{
                      fontSize: 10,
                      cursor: "pointer",
                      color: "purple.main",
                    }}
                  >
                    on annual plan
                  </Box>
                </Tooltip>
              </Typography>
            </>,
          )}
        {renderRow(
          <></>,
          <></>,
          <Grid item xs={12} sx={{ mb: -1, pr: 1 }}>
            <Button
              onClick={() => handleCheckout(Plans.PRO_YEARLY)}
              disableElevation
              fullWidth
              variant="contained"
              sx={{ mx: 0.5, borderRadius: 4 }}
              disabled={isAlreadyOnPro}
            >
              {isAlreadyOnPro ? "Already Subscribed" : "Upgrade Now"}
            </Button>
            {renderDiscount()}
          </Grid>,
          <Grid item xs={12} sx={{ mb: -1, pr: 1 }}>
            <Button
              disableElevation
              fullWidth
              variant="contained"
              onClick={() => handleCheckout(Plans.VA_YEARLY)}
              sx={{ mx: 0.5, borderRadius: 4 }}
              disabled={isAlreadyOnVa}
            >
              {isAlreadyOnVa ? "Already Subscribed" : "Upgrade Now"}
            </Button>
            {renderDiscount()}
            <Typography
              textAlign="center"
              sx={{ fontSize: 12, my: 1, color: "black", px: 2 }}
            >
              ⚠️ Virtual Assistant will increase to <br />{" "}
              <strong>$72/mo (or $650</strong>/yr) after {getNextFriday()}
            </Typography>
          </Grid>,
          true,
        )}
      </Grid>
    </DialogContent>
  );
};

export default OnboardingPaywall;
